import { useIntl } from 'react-intl';
import { Schedule } from '@/features/accounts/types';
import { actionable } from '@/features/accounts/schedule/actionable';
import { type ScheduleControls } from '@/features/accounts/schedule/hooks/useScheduleControls';
import ClockIcon from '@/shared/components/ClockIcon';
import CogIcon from '@/shared/components/CogIcon';
import PauseIcon from '@/shared/components/PauseIcon';
import PlayIcon from '@/shared/components/PlayIcon';

interface Props {
  schedule: Schedule | null;
  actions: ScheduleControls;
}

export default function ActionsCell({ schedule, actions }: Props) {
  const intl = useIntl();

  if (!schedule || !actionable(schedule)) return null;

  const iconButtonClass = 'rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600';

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => actions.viewActivities(schedule)}
        className={iconButtonClass}
        aria-label={intl.formatMessage({ id: 'schedule.activities.title' })}
      >
        <ClockIcon />
      </button>
      {schedule.status === 'active' && (
        <>
          <button
            type="button"
            onClick={() => actions.configure(schedule)}
            className={iconButtonClass}
            aria-label={intl.formatMessage({ id: 'schedule.list.configure' })}
          >
            <CogIcon />
          </button>
          <button
            type="button"
            onClick={() => actions.pause(schedule.id)}
            className="rounded p-1 text-red-400 transition hover:bg-slate-100 hover:text-red-600"
            aria-label={intl.formatMessage({ id: 'schedule.list.pause' })}
          >
            <PauseIcon />
          </button>
        </>
      )}
      {schedule.bookable && schedule.status === 'paused' && (
        <button
          type="button"
          onClick={() => actions.activate(schedule)}
          className="rounded p-1 text-green-500 transition hover:bg-slate-100 hover:text-green-700"
          aria-label={intl.formatMessage({ id: 'schedule.list.activate' })}
        >
          <PlayIcon />
        </button>
      )}
      {schedule.bookable && schedule.reactivatable && (
        <button
          type="button"
          onClick={() => actions.reactivate(schedule)}
          className="rounded p-1 text-green-500 transition hover:bg-slate-100 hover:text-green-700"
          aria-label={intl.formatMessage({ id: 'schedule.list.reactivate' })}
        >
          <PlayIcon />
        </button>
      )}
    </div>
  );
}
